import React, { useEffect, useState, useCallback } from 'react';
import { RefreshCw, MessageSquare, Ticket } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const TIER_LABELS = {
  anonymous:   'Anon',
  free_trial:  'Free trial',
  essentials:  'Essentials',
  guided:      'Guided',
  community:   'Community',
  connected:   'Connected',
};

/**
 * Admin → Upgrade Insights
 *
 * Shows the reason breakdown from the exit-intent micro-survey, plus
 * per-tier filter chips, recent verbatim free-text, and conditional
 * Stripe-coupon offer stats (issued / emailed / redeemed / active).
 */
export default function AdminUpgradeInsightsPanel() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [tier, setTier] = useState(null); // null = all

  const load = useCallback(async (selectedTier) => {
    setLoading(true); setErr(null);
    try {
      const qs = selectedTier ? `?tier=${encodeURIComponent(selectedTier)}` : '';
      const r = await fetch(`${API_URL}/api/admin/upgrade-insights${qs}`, { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setD(await r.json());
    } catch (e) { setErr(e.message || 'Could not load insights'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(tier); }, [load, tier]);

  const coupons = d?.coupons || {};

  return (
    <div data-testid="admin-upgrade-insights-panel" className="space-y-6">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#1A5C6E] mb-1">
            Admin · Conversion
          </p>
          <h2
            className="font-display italic text-3xl text-[#0A1F2E] leading-tight"
            data-testid="upgrade-insights-headline"
          >
            Why didn't they upgrade?
          </h2>
          <p className="font-body text-sm text-[#0A1F2E]/65 mt-1">
            Reasons collected from the exit-intent survey when users dismiss the upgrade wall.
          </p>
        </div>
        <button
          onClick={() => load(tier)}
          disabled={loading}
          data-testid="upgrade-insights-refresh"
          className="inline-flex items-center gap-2 border border-[#0A1F2E]/15 hover:border-[#2CA8A0] text-[#0A1F2E] font-mono uppercase text-[10px] tracking-[0.15em] py-2.5 px-4 rounded-full transition disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </header>

      {err && (
        <p className="font-body text-sm text-red-700 bg-red-50 border-l-4 border-red-400 px-4 py-2 rounded-r">
          {err}
        </p>
      )}

      {/* Tier filter chips */}
      <div
        className="flex flex-wrap gap-2"
        data-testid="upgrade-insights-tier-chips"
        role="tablist"
        aria-label="Filter by tier at event"
      >
        <button
          type="button"
          onClick={() => setTier(null)}
          data-testid="tier-chip-all"
          className={`px-3 py-1.5 rounded-full text-[11px] font-mono uppercase tracking-[0.1em] transition
            ${tier === null
              ? 'bg-[#0A1F2E] text-[#F9F6EF] font-semibold'
              : 'bg-white text-[#0A1F2E] border border-[#EFE6CF] hover:border-[#2CA8A0]'}`}
        >
          All <span className="ml-1 opacity-70">({d?.total ?? 0})</span>
        </button>
        {(d?.tier_chips || []).map((t) => (
          <button
            key={t.tier}
            type="button"
            onClick={() => setTier(t.tier)}
            data-testid={`tier-chip-${t.tier}`}
            className={`px-3 py-1.5 rounded-full text-[11px] font-mono uppercase tracking-[0.1em] transition
              ${tier === t.tier
                ? 'bg-[#E8A830] text-[#0A1F2E] font-semibold'
                : 'bg-white text-[#0A1F2E] border border-[#EFE6CF] hover:border-[#E8A830]'}`}
          >
            {TIER_LABELS[t.tier] || t.tier} <span className="ml-1 opacity-70">({t.count})</span>
          </button>
        ))}
      </div>

      {/* Reason breakdown */}
      <section
        className="bg-white border border-[#EFE6CF] rounded-xl p-5"
        data-testid="upgrade-insights-breakdown"
      >
        <p className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/70 mb-3">
          {tier ? `${TIER_LABELS[tier] || tier} responses` : 'Total responses'}:{' '}
          <span className="text-[#0A1F2E] font-semibold">{d?.total ?? 0}</span>
        </p>
        {(!d || d.breakdown.length === 0) ? (
          <p className="font-body text-sm italic text-[#0A1F2E]/55">No responses yet - the survey fires when a user dismisses the upgrade wall.</p>
        ) : (
          <ul className="space-y-2.5">
            {d.breakdown.map((b) => (
              <li
                key={b.reason}
                data-testid={`upgrade-insights-row-${b.reason}`}
                className="flex items-center gap-3"
              >
                <span className="font-body text-sm text-[#0A1F2E] w-56">{b.label}</span>
                <div className="flex-1 h-2 bg-[#F4ECD8] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#E8A830]"
                    style={{ width: `${Math.min(b.pct, 100)}%` }}
                  />
                </div>
                <span className="font-mono text-xs text-[#E8A830] font-semibold w-12 text-right">{b.pct}%</span>
                <span className="font-mono text-xs text-[#0A1F2E]/55 w-8 text-right">{b.count}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Conditional offer (15% off) - coupon tracking */}
      <section
        className="bg-white border border-[#EFE6CF] rounded-xl p-5"
        data-testid="upgrade-insights-coupons"
      >
        <h3 className="font-display italic text-xl text-[#0A1F2E] mb-1 flex items-center gap-2">
          <Ticket className="w-4 h-4 text-[#E8A830]" />
          Conditional 15% offer
        </h3>
        <p className="font-body text-xs text-[#0A1F2E]/55 mb-4">
          Fired when a user picks <em>"The price is too high"</em>. One-time Stripe coupon, 48 h expiry.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { k: 'issued',   label: 'Issued' },
            { k: 'emailed',  label: 'Emailed' },
            { k: 'redeemed', label: 'Redeemed' },
            { k: 'active',   label: 'Active' },
          ].map(({ k, label }) => (
            <div
              key={k}
              className="bg-[#FFF9EC] border border-[#EFE6CF] rounded-lg p-3"
              data-testid={`coupon-stat-${k}`}
            >
              <p className="font-mono uppercase text-[9px] tracking-[0.18em] text-[#1A5C6E]">{label}</p>
              <p className="font-display italic text-2xl text-[#0A1F2E] mt-0.5">{coupons[k] ?? 0}</p>
            </div>
          ))}
        </div>
      </section>

      {(d?.recent_free_text || []).length > 0 && (
        <section
          className="bg-white border border-[#EFE6CF] rounded-xl p-5"
          data-testid="upgrade-insights-free-text"
        >
          <h3 className="font-display italic text-xl text-[#0A1F2E] mb-3 flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-[#1A5C6E]" />
            Recent verbatim
          </h3>
          <ul className="space-y-3">
            {d.recent_free_text.map((r) => (
              <li
                key={r.id}
                className="border-l-2 border-[#E8A830] pl-3 py-1"
                data-testid={`upgrade-insights-quote-${r.id}`}
              >
                <p className="font-body text-sm text-[#0A1F2E] italic leading-relaxed">"{r.free_text}"</p>
                <p className="font-mono text-[10px] text-[#0A1F2E]/45 mt-1">
                  {r.email || 'anon'} · {r.tier_at_event || 'anon'} · {r.created_at?.slice(0, 10)}
                </p>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
